import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

console.log('Rendering ProjectShowcase');

const ProjectShowcase = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
  {
    id: 1,
    title: "FinTech Payment Gateway",
    category: "fintech",
    description: "Secure payment processing system handling $2M+ daily transactions with 99.99% uptime",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13913910e-1764639852428.png",
    imageAlt: "Modern financial dashboard showing payment analytics with blue and green charts on multiple screens in professional office",
    tags: ["React", "Node.js", "PostgreSQL"],
    metrics: { performance: "+250%", users: "100K+", uptime: "99.99%" }
  },
  {
    id: 2,
    title: "Healthcare Patient Portal",
    category: "healthcare",
    description: "HIPAA-compliant patient management system serving 50+ medical facilities",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_195a6e902-1764648555298.png",
    imageAlt: "Healthcare professional using tablet displaying patient records and medical charts in modern hospital setting with blue medical equipment",
    tags: ["Next.js", "MongoDB", "AWS"],
    metrics: { facilities: "50+", patients: "200K+", compliance: "HIPAA" }
  },
  {
    id: 3,
    title: "E-Learning Platform",
    category: "education",
    description: "Interactive learning management system with real-time collaboration features",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e32c9d5e-1766805495998.png",
    imageAlt: "Student using laptop showing colorful online learning interface with video lessons and interactive quizzes in bright study room",
    tags: ["React", "WebSocket", "Redis"],
    metrics: { students: "75K+", courses: "500+", engagement: "+180%" }
  },
  {
    id: 4,
    title: "Real Estate Marketplace",
    category: "marketplace",
    description: "Property listing platform with advanced search and virtual tour capabilities",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1347ab334-1764674767749.png",
    imageAlt: "Modern luxury home exterior with large windows and landscaped garden displayed on tablet screen held by real estate agent",
    tags: ["Vue.js", "Express", "MySQL"],
    metrics: { listings: "10K+", views: "1M+", conversions: "+65%" }
  },
  {
    id: 5,
    title: "Supply Chain Dashboard",
    category: "enterprise",
    description: "Real-time logistics tracking system optimizing delivery routes and inventory",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_135ea089a-1764637482598.png",
    imageAlt: "Warehouse worker using tablet showing supply chain management dashboard with inventory levels and shipping routes on large screens",
    tags: ["Angular", "Python", "Docker"],
    metrics: { efficiency: "+40%", cost: "-30%", accuracy: "99.5%" }
  },
  {
    id: 6,
    title: "Social Media Analytics",
    category: "saas",
    description: "Comprehensive social media management tool with AI-powered insights",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_179a29bb3-1764669852473.png",
    imageAlt: "Marketing team analyzing social media metrics on large monitor showing engagement graphs and follower statistics in modern office",
    tags: ["React", "GraphQL", "TensorFlow"],
    metrics: { clients: "500+", posts: "10M+", accuracy: "95%" }
  }];


  const filters = [
  { id: 'all', label: 'All Projects', icon: 'Grid3x3' },
  { id: 'fintech', label: 'FinTech', icon: 'DollarSign' },
  { id: 'healthcare', label: 'Healthcare', icon: 'Heart' },
  { id: 'education', label: 'Education', icon: 'GraduationCap' },
  { id: 'marketplace', label: 'Marketplace', icon: 'ShoppingCart' },
  { id: 'enterprise', label: 'Enterprise', icon: 'Building2' },
  { id: 'saas', label: 'SaaS', icon: 'Cloud' }];


  const filteredProjects = activeFilter === 'all' ?
  projects :
  projects?.filter((p) => p?.category === activeFilter);

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4">
            Production-Grade Projects
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Real-world solutions delivering measurable business impact across industries
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12">
          {filters?.map((filter) =>
          <button
            key={filter?.id}
            onClick={() => setActiveFilter(filter?.id)}
            className={`
                flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium text-sm md:text-base
                transition-all duration-300
                ${activeFilter === filter?.id ?
            'bg-primary text-primary-foreground shadow-lg' :
            'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'}
              `
            }>

              <Icon name={filter?.icon} size={18} />
              <span>{filter?.label}</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredProjects?.map((project) =>
          <div
            key={project?.id}
            className="group bg-card rounded-xl overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">

              <div className="relative aspect-[16/10] overflow-hidden">
                <Image
                src={project?.image}
                alt={project?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              <div className="p-4 md:p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  {project?.tags?.map((tag) =>
                <span
                  key={tag}
                  className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded">

                      {tag}
                    </span>
                )}
                </div>

                <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 line-clamp-1">
                  {project?.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-4 line-clamp-2">
                  {project?.description}
                </p>

                <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-border">
                  {Object.entries(project?.metrics)?.map(([key, value]) =>
                <div key={key} className="text-center">
                      <div className="text-sm md:text-base font-bold text-primary whitespace-nowrap">
                        {value}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {key}
                      </div>
                    </div>
                )}
                </div>

                <Button
                variant="outline"
                size="sm"
                iconName="ArrowRight"
                iconPosition="right"
                className="w-full"
                asChild>

                  <Link to="/portfolio">View Details</Link>
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-12 md:mt-16">
          <Button
            variant="default"
            size="lg"
            iconName="FolderOpen"
            iconPosition="right"
            asChild>

            <Link to="/portfolio">View All Projects</Link>
          </Button>
        </div>
      </div>
    </section>);

};

export default ProjectShowcase;